import { html, useState } from '/vendor/htm-preact.module.js';
import { post } from '/api.js';

/**
 * Putting somebody in a circle.
 *
 * Shared by the record screen and the leaderboard. Who is in a circle is not a
 * property of recording a match, but it was only ever reachable from there —
 * so a circle you were not currently recording into could never gain a member.
 *
 * Both ways of adding are here because they are not interchangeable. Somebody
 * with an account joins AS THEMSELVES; inventing a guest for them would fork
 * their history in two and the head-to-head would never meet.
 *
 * `prefix` names the test ids, so the two hosts can be told apart.
 */
// Above this many, the dropdown becomes a search. The same number and the
// same reasoning as admin.js, about the same population: a `<select>` you
// have to scroll for a minute is not a menu, and a search box shown to
// somebody choosing between four people is a worse control than the list.
//
// Deliberately NOT a straight conversion to search. `/api/people` returns
// thirteen accounts on the live instance, and for thirteen the dropdown is
// the better control -- you can see everybody without typing, and without
// having to know a name to type. The archive holds 1,554 players, so the
// day a useful number of them have accounts this screen already copes.
const BROWSE_LIMIT = 25;

export function AddToCircle({ circleId, addable, onAdded, prefix,
                              ask: mayAsk = true }) {
  const [personId, setPersonId] = useState('');
  const [guest, setGuest] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [asked, setAsked] = useState(false);
  const [search, setSearch] = useState('');

  const crowded = addable.length > BROWSE_LIMIT;
  const query = search.trim().toLowerCase();
  // Client-side over a list already in hand, the same choice the people
  // search and the stats board make: the work per keystroke is one pass
  // over a few hundred short strings, and a round trip would buy nothing.
  const matches = query
    ? addable.filter((a) => (a.name || '').toLowerCase().includes(query))
    : [];

  const guard = (fn) => async () => {
    setBusy(true); setError('');
    try { await fn(); }
    catch (e) { setError(e.detail || e.message); }
    finally { setBusy(false); }
  };

  // A GUEST is added; a PERSON is asked. GitHub issue #63 -- being put into
  // somebody else's circle without being consulted is the thing that
  // changed. A placeholder has no account and no way to answer, so inventing
  // one for the walk-up at the table stays immediate or the feature it
  // exists for stops working.
  //
  // `ask` IS FALSE ON THE RECORD SCREEN, and that is not a loophole. Ben
  // asked for this "if you are managing a circle"; recording is the other
  // situation entirely -- you are standing at a table with somebody, about
  // to play them. Waiting for them to accept on their phone before the game
  // can be written down would break recording against a real account
  // outright. And that path has its own consent already: a match against a
  // member is PROVISIONAL until they confirm it, so the game does not count
  // for either of you until they say it happened.
  const join = async (userId) => {
    await post(`/api/circles/${circleId}/members`, { user_id: userId });
    await onAdded(userId);
  };

  const ask = async (userId) => {
    await post(`/api/circles/${circleId}/invites`, { user_id: userId });
    setAsked(true);
    // NO `onAdded` HERE, and that is the fix for a race rather than a
    // shortcut. Asking somebody changes no membership -- they are not in the
    // circle until they say yes -- so there is nothing for the parent to
    // refetch. And refetching actively destroyed the confirmation: the
    // circle screen sets `rows` to null while it reloads and early-returns
    // "Loading...", which unmounts this component and takes `asked` with it.
    // The message survived only if the screen was sampled in the gap, which
    // is why it passed on a quiet machine and failed on a busy one.
  };

  const addPerson = guard(async () => {
    // The select shows its first option before anything is chosen, so an
    // untouched dropdown has to mean that option and not "nobody".
    const id = personId || (addable[0] && addable[0].user_id);
    if (!id) return;
    setPersonId('');
    await (mayAsk ? ask(id) : join(id));
  });

  // The search's rows name their person outright, so unlike the dropdown
  // there is no "what was selected" to resolve -- and no way to press Ask
  // and reach somebody other than the one whose row you pressed.
  const pick = (userId) => guard(async () => {
    setSearch('');
    await (mayAsk ? ask(userId) : join(userId));
  });

  const addGuest = guard(async () => {
    // "Asked. They join once they say yes." is about the LAST person, and a
    // guest is added outright -- leaving it up would tell somebody their
    // walk-up opponent is waiting to accept an invitation they never got.
    setAsked(false);
    if (!guest.trim()) return;
    const id = (await post('/api/users/ghost', { name: guest })).user_id;
    setGuest('');
    await join(id);
  });

  return html`
    ${addable.length > 0 && (crowded
      ? html`
        ${/* One press per person, so there is no "which one was selected"
              to get wrong -- the name you tap is the name that is asked. */''}
        <div class="stack">
          <input data-testid=${`${prefix}-add-search`} value=${search}
                 placeholder="Search people"
                 onInput=${(e) => setSearch(e.target.value)} />
          ${!query && html`
            <p class="sub" data-testid=${`${prefix}-add-browse`}
               style="text-transform:none">
              ${`${addable.length} people could be added. `
                + `Type a name to find one.`}
            </p>`}
          ${query && matches.length === 0 && html`
            <p class="empty" data-testid=${`${prefix}-add-none`}>
              Nobody with an account is called that.
            </p>`}
          ${matches.map((a) => html`
            <div key=${a.user_id} class="card rowbetween">
              <span>${a.name}</span>
              <button class="secondary" style="width:auto" disabled=${busy}
                      data-testid=${`${prefix}-add-candidate-${a.user_id}`}
                      onClick=${pick(a.user_id)}>
                ${mayAsk ? 'Ask' : 'Add'}
              </button>
            </div>`)}
        </div>`
      : html`
        <div class="row">
          <select data-testid=${`${prefix}-add-person`}
                  value=${personId || addable[0].user_id}
                  onChange=${(e) => setPersonId(e.target.value)}>
            ${addable.map((a) => html`
              <option value=${a.user_id}>${a.name}</option>`)}
          </select>
          <button class="secondary" data-testid=${`${prefix}-add-person-btn`}
                  disabled=${busy} onClick=${addPerson}>
            ${mayAsk ? 'Ask' : 'Add'}
          </button>
        </div>`)}
    ${addable.length > 0 && asked && html`
      <p class="sub" data-testid=${`${prefix}-asked`}
         style="text-transform:none">
        Asked. They join once they say yes.
      </p>`}

    <div class="row pair">
      <input data-testid=${`${prefix}-new-opponent`}
             placeholder="Someone with no account" value=${guest}
             onInput=${(e) => setGuest(e.target.value)} />
      <button class="secondary" data-testid=${`${prefix}-add-guest`}
              disabled=${busy} onClick=${addGuest}>Add</button>
    </div>

    ${error && html`<p class="error" data-testid=${`${prefix}-add-error`}>
                      ${error}
                    </p>`}`;
}
